import React, { useEffect, useState } from 'react'
import { Breadcrumb, Button, Container, Table } from 'react-bootstrap'
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ControlPanelManager } from '../../../api/ControlPanelManager';
import { useToasts } from '../../../contexts/toasts';
import { Alert, Card, Col, Modal, Row, Tag } from 'antd';
import { useCallback } from 'react';
import UserItem from '../../../components/common/UserItem';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useFullScreenLoader } from '../../../contexts/fullScreenLoader';
import { User } from '../../../models/User';
import { Group } from '../../../models/ControlPanel';
import AppLoader from '../../../components/common/AppLoader';


export default function UserDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [user, setUser] = useState<User>();
  const [groups, setGroups] = useState<Group[]>();
  const [organizations, setOrganizations] = useState<Group[]>();
  const [permissions, setPermissions] = useState<Group[]>();
  const [error, setError] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();

  const initUser = useCallback(() => {
    ControlPanelManager.getUser(id!)
      .then(
        ({ data }) => setUser(data),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('user-error', message, `User Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, showToast]);

  const getUserGroups = useCallback(() => {
    ControlPanelManager.getUserGroups(id!)
      .then(
        ({ data }) => {
          const pp = data.filter(group => group.path.startsWith('/Permissions'));
          setPermissions(pp);
          const orgs = data.filter(group => group.path.startsWith('/Organizations'));
          setOrganizations(orgs);
          // TODO remove groups after users migration
          if (pp.length === 0 && orgs.length === 0) setGroups(data);
        },
        (err) => {
          // setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('user-groups-error', message, `User Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, showToast]);

  useEffect(() => {
    if (id) {
      initUser();
      getUserGroups();
    }
    // eslint-disable-next-line
  }, [id]);

  const openDeleteModal = useCallback(() => setShowDeleteModal(true), []);
  const handleDeleteModalClose = useCallback(() => setShowDeleteModal(false), []);

  const deleteUser = useCallback(() => {
    showLoader();

    ControlPanelManager.deleteUser(user!.id!)
      .then(
        () => {
          hideLoader();
          showToast('user-success', 'User deleted successfully', 'User Success', 'success');
          handleDeleteModalClose();
          navigate("/control-panel/users-management");
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('users-error', message, `Users Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [user, showLoader, hideLoader, showToast, navigate, handleDeleteModalClose]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert
        type="error"
        showIcon
        message="An error occurred while retrieving user. Try again later."
      />
    )

    if (!user) return <AppLoader className="py-5 my-5" justify="center" size={100} />;

    return (
      <>
        <Row align="middle" className="mb-3">
          <Col flex="auto">
            <UserItem title={user!.username} subtitle={user!.email} />
          </Col>
          {!user!.enabled && (
            <Col md={12} lg={14} xl={18}>
              <Tag color="yellow" icon={<FontAwesomeIcon icon={solid('exclamation-triangle')} className="me-2" />}>User disabled</Tag>
            </Col>
          )}
          <Link to="edit">
            <Button className="border me-3" variant="light" size="sm">
              <FontAwesomeIcon icon={solid('pen')} />
              <span className="ms-2">Edit</span>
            </Button>
          </Link>
          <Button variant="danger" size="sm" onClick={openDeleteModal}>
            <FontAwesomeIcon icon={solid('trash')} />
            <span className="ms-2">Delete</span>
          </Button>
        </Row>

        <Table>
          <tbody>
            <tr>
              <th scope="col">First Name</th>
              <td className="text-end text-muted">{user!.firstName || '--'}</td>
            </tr>
            <tr>
              <th scope="col">Last Name</th>
              <td className="text-end text-muted">{user!.lastName || '--'}</td>
            </tr>
            {permissions && permissions.length > 0 && (
              <tr>
                <th scope="col">Roles</th>
                <td className="text-end">{permissions.map(role => <Tag className="me-0 ms-2" key={role.id} icon={<FontAwesomeIcon className="me-1" icon={role.name.includes("Admin") ? solid('crown') : solid('ranking-star')} />} color="orange">{role.name}</Tag>)}</td>
              </tr>
            )}
            {organizations && organizations.length > 0 && (
              <tr>
                <th scope="col">Organizations</th>
                <td className="text-end">{organizations.map(group => <Tag className="me-0 ms-2" key={group.id} icon={<FontAwesomeIcon className="me-1" icon={solid('building-user')} />} color="blue">{group.name}</Tag>)}</td>
              </tr>
            )}
            {groups && (
              <tr>
                <th scope="col">Groups</th>
                <td className="text-end">{groups.map(group => <Tag className="me-0 ms-2" icon={<FontAwesomeIcon className="me-1" icon={solid('users')} />} color="green">{group.name}</Tag>)}</td>
              </tr>
            )}
          </tbody>
        </Table>
      </>
    )
  }, [error, user, organizations, permissions, groups, openDeleteModal]);

  return (
    <Container className="py-3">
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/control-panel' }}>Control Panel</Breadcrumb.Item>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/control-panel/users-management' }}>User Management</Breadcrumb.Item>
        {id && <Breadcrumb.Item active>{user?.username}</Breadcrumb.Item>}
      </Breadcrumb>

      <Card>{renderAsyncContent()}</Card>

      <Modal
        title="Delete User"
        open={showDeleteModal}
        onCancel={handleDeleteModalClose}
        afterClose={handleDeleteModalClose}
        footer={
          <div className="d-flex justify-content-between mt-3">
            <Button size="sm" variant="light" className="border" onClick={handleDeleteModalClose}>Cancel</Button>
            <Button size="sm" variant="danger" onClick={deleteUser}>
              <FontAwesomeIcon icon={solid('trash')} />
              <span className="ms-2">Delete</span>
            </Button>
          </div>
        }
      >
        <p>Are you sure you want to delete user <b>{user?.username}</b>?</p>

        <Alert
          type="warning"
          showIcon
          message={
            <div className="ps-1">
              <p className="mb-0">Remember, this operation cannot be undone</p>
              <b>User's private resources will be permanently deleted</b>
            </div>
          }
        />
      </Modal>
    </Container>
  )
}